import {Field, Formik, FormikHelpers as FormikActions, FormikProps} from 'formik';
import React, {useEffect, useMemo, useState} from 'react';
import * as Yup from 'yup';
import {Button} from '~/components/Button';
import {DateRangePickerInput} from '~/components/Controls/DatePicker';
import {useConfirm} from '~/components/Dialogs';
import {
    Form,
    FormColumn,
    FormControls,
    FormField,
    FormRow,
    FormTitle,
    EditorField,
    IFieldProps,
    InputField,
    IntentText,
    SelectField,
    TextareaField
} from '~/components/Form';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {Intent} from '~/lib/intent';
import {PercentageField} from '~/modules/BusinessIncentives/components/PercentageField';
import {UsdInputField} from '~/modules/BusinessIncentives/components/UsdInputField';
import {
    IBusinessIncentive,
    ITierConfig,
    IncentiveState,
    incentiveStateLabel,
    IncentiveStructure,
    incentiveStructureLabel,
    IncentiveType,
    incentiveTypeLabel,
    structureRequiresTier,
    structureRequiresValue
} from '~/modules/BusinessIncentives/models/BusinessIncentive';
import {hasStartedPeriod, isDateInFuture} from '~/modules/BusinessIncentives/utils/period';
import style from './BusinessIncentiveForm.module.scss';

const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

const parseIsoDate = (value?: string): Date | undefined => {
    if (!value || !dateRegex.test(value)) {
        return undefined;
    }
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
        return undefined;
    }
    return parsed;
};

const toIsoDate = (date?: Date): string => {
    if (!date || Number.isNaN(date.getTime())) {
        return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getTodayIsoDate = (): string => toIsoDate(new Date());

const getTodayDate = (): Date => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const normalizeTier = (tier: Partial<ITierConfig> | null | undefined): ITierConfigFormValue => ({
    name: (tier?.name ?? '').trim(),
    thresholdUsd: typeof tier?.thresholdUsd === 'number' && Number.isFinite(tier.thresholdUsd) ? tier.thresholdUsd : 0,
    percentage: typeof tier?.percentage === 'number' && Number.isFinite(tier.percentage) ? tier.percentage : 0,
});

export interface ITierConfigFormValue {
    name: string;
    thresholdUsd: number;
    percentage: number;
}

export interface IBusinessIncentiveFormValues {
    id: number | null;
    incentiveType: IncentiveType;
    structure: IncentiveStructure;
    scope: string;
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    state: IncentiveState;
    currentTier: string;
    tiers: ITierConfigFormValue[];
    value: number | '';
}

export interface IBusinessIncentiveFormProps {
    incentive?: IBusinessIncentive;
    onSubmit?: (
        values: IBusinessIncentiveFormValues,
        actions: FormikActions<IBusinessIncentiveFormValues>,
        close: IModalComponentClose
    ) => void;
}

interface IBusinessIncentiveFormBodyProps {
    formik: FormikProps<IBusinessIncentiveFormValues>;
    close: IModalComponentClose;
    isEditMode: boolean;
    isPersistedClosed: boolean;
    isEndDateEditable: boolean;
}

interface IBusinessIncentivePeriodFieldOptions {
    disabled?: boolean;
    endDateOnly?: boolean;
}

const buildFormSchema = (isEditMode: boolean, isEndDateEditable: boolean) => Yup.object().shape({
    id: Yup.number().nullable(true),
    incentiveType: Yup.mixed<IncentiveType>().required().oneOf(Object.values(IncentiveType)),
    structure: Yup.mixed<IncentiveStructure>().required().oneOf(Object.values(IncentiveStructure)),
    scope: Yup.string().required(),
    name: Yup.string().required().max(200),
    description: Yup.string().required().max(2000),
    startDate: Yup.string().required().matches(dateRegex, 'must be in YYYY-MM-DD format')
        .test('start-not-before-today', 'Start date must be today or later', function (startDate) {
            if (isEditMode && hasStartedPeriod(startDate)) {
                return true;
            }
            if (!dateRegex.test(startDate ?? '')) {
                return true;
            }
            return (startDate ?? '') >= getTodayIsoDate();
        }),
    endDate: Yup.string().required().matches(dateRegex, 'must be in YYYY-MM-DD format')
        .test('end-not-before-today',
            isEndDateEditable ? 'End date must be after today' : 'End date must be today or later',
            endDate => {
            if (!dateRegex.test(endDate ?? '')) {
                return true;
            }
            if (isEditMode && !isEndDateEditable) {
                return true;
            }
            return isEndDateEditable
                ? (endDate ?? '') > getTodayIsoDate()
                : (endDate ?? '') >= getTodayIsoDate();
        })
        .test('end-not-before-start', 'End date must be same or later than start date', function (endDate) {
            const startDate = this.parent.startDate;
            if (!dateRegex.test(startDate) || !dateRegex.test(endDate ?? '')) {
                return true;
            }
            const startValue = Date.parse(`${startDate}T00:00:00Z`);
            const endValue = Date.parse(`${endDate}T00:00:00Z`);
            return endValue >= startValue;
        }),
    state: Yup.mixed<IncentiveState>().required().oneOf(Object.values(IncentiveState))
        .test('state-for-tier-structures', 'must be active or closed for tier structures', function (state) {
            const parent = this.parent as IBusinessIncentiveFormValues;
            if (!structureRequiresTier(parent.structure)) {
                return true;
            }
            return state !== IncentiveState.INACTIVE;
        }),
    currentTier: Yup.string().max(120)
        .test('current-tier-exists', 'must match one of tier types', function (currentTier) {
            const parent = this.parent as IBusinessIncentiveFormValues;
            if (!structureRequiresTier(parent.structure)) {
                return true;
            }
            const normalizedCurrentTier = (currentTier ?? '').trim();
            if (!normalizedCurrentTier) {
                return true;
            }
            const tierNames = parent.tiers.map(tier => tier.name.trim()).filter(Boolean);
            return tierNames.includes(normalizedCurrentTier);
        }),
    tiers: Yup.array().of(Yup.object().shape({
        name: Yup.string().trim().required().max(120),
        thresholdUsd: Yup.number().typeError('must be a number').required().min(0),
        percentage: Yup.number().typeError('must be a number').required().min(0).max(100),
    }))
        .test('tiers-required-for-structure', 'at least one tier type is required', function (tiers) {
            const structure = (this.parent as IBusinessIncentiveFormValues).structure;
            if (!structureRequiresTier(structure)) {
                return true;
            }
            return Array.isArray(tiers) && tiers.length > 0;
        })
        .test('tier-types-unique', 'tier types must be unique', function (tiers) {
            const structure = (this.parent as IBusinessIncentiveFormValues).structure;
            if (!structureRequiresTier(structure)) {
                return true;
            }
            const normalizedTierNames = (Array.isArray(tiers) ? tiers : [])
                .map(tier => String((tier as Partial<ITierConfigFormValue>)?.name ?? '').trim())
                .filter(Boolean);
            return normalizedTierNames.length === new Set(normalizedTierNames).size;
        })
        .test('first-tier-threshold-zero', 'first tier threshold must be 0', function (tiers) {
            const structure = (this.parent as IBusinessIncentiveFormValues).structure;
            if (!structureRequiresTier(structure)) {
                return true;
            }
            if (!Array.isArray(tiers) || tiers.length === 0) {
                return true;
            }
            const firstThreshold = Number((tiers[0] as Partial<ITierConfigFormValue>)?.thresholdUsd ?? 0);
            return firstThreshold === 0;
        }),
    value: Yup.mixed<number | ''>()
        .test('value-required-for-non-tier', 'is required', function (value) {
            const structure = (this.parent as IBusinessIncentiveFormValues).structure;
            if (!structureRequiresValue(structure)) {
                return true;
            }
            return value !== '' && value !== null && value !== undefined;
        })
        .test('value-number-min', 'must be greater than or equal to 0', function (value) {
            const structure = (this.parent as IBusinessIncentiveFormValues).structure;
            if (!structureRequiresValue(structure) || value === '' || value === null || value === undefined) {
                return true;
            }
            const parsed = Number(value);
            return Number.isFinite(parsed) && parsed >= 0;
        })
        .test('value-not-allowed-for-tier', 'must be empty for tier-based structures', function (value) {
            const structure = (this.parent as IBusinessIncentiveFormValues).structure;
            if (!structureRequiresTier(structure)) {
                return true;
            }
            return value === '' || value === null || value === undefined;
        })
});

const scopeToFormValue = (scope: IBusinessIncentive['scope']): string => {
    if (typeof scope === 'string') {
        return scope;
    }
    if (scope && typeof scope === 'object') {
        try {
            return JSON.stringify(scope, null, 2);
        } catch {
            return String(scope);
        }
    }
    return '';
};

const normalizeCurrentTier = (value: string | null | undefined): string => value ?? '';
const createDefaultTier = (): ITierConfigFormValue => normalizeTier({name: ''});

const typeItems = Object.values(IncentiveType).map(type => ({
    label: incentiveTypeLabel[type],
    value: type,
}));

const structureItems = Object.values(IncentiveStructure).map(structure => ({
    label: incentiveStructureLabel[structure],
    value: structure,
}));

const PeriodField = (props: IFieldProps<IBusinessIncentivePeriodFieldOptions>) => {
    const values = props.form.values as IBusinessIncentiveFormValues;
    const minDate = useMemo(() => {
        if (!props.options.endDateOnly) {
            return getTodayDate();
        }
        const today = getTodayDate();
        const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
        const startDate = parseIsoDate(values.startDate);

        if (!startDate || startDate <= tomorrow) {
            return tomorrow;
        }
        return startDate;
    }, [props.options.endDateOnly, values.startDate]);
    const period = {
        startDate: parseIsoDate(values.startDate),
        endDate: parseIsoDate(values.endDate),
    };

    const periodErrors: string[] = [];
    if (props.form.submitCount > 0) {
        const startError = typeof props.form.errors.startDate === 'string' ? props.form.errors.startDate : null;
        const endError = typeof props.form.errors.endDate === 'string' ? props.form.errors.endDate : null;

        if (startError && endError) {
            if (startError === endError) {
                periodErrors.push(startError);
            } else {
                periodErrors.push(startError);
                periodErrors.push(endError);
            }
        } else if (startError) {
            periodErrors.push(startError);
        } else if (endError) {
            periodErrors.push(endError);
        }
    }

    const formFieldProps = {
        ...props,
        form: {
            ...props.form,
            errors: {
                ...props.form.errors,
                startDate: undefined,
            },
        },
    };

    return (
        <FormField {...formFieldProps}>
            <DateRangePickerInput
                dateRange={period}
                allowFutureDates
                dateFormat='yyyy-MM-dd'
                fullWidth
                minDate={minDate}
                disabled={props.options.disabled}
                mode={props.options.endDateOnly ? 'end-only' : 'range'}
                onChange={(dateRange) => {
                    const nextStartDate = toIsoDate(dateRange.startDate);
                    const nextEndDate = toIsoDate(dateRange.endDate);

                    if (!props.options.endDateOnly && nextStartDate !== values.startDate) {
                        props.form.setFieldValue('startDate', nextStartDate, false);
                    }
                    if (nextEndDate !== values.endDate) {
                        props.form.setFieldValue('endDate', nextEndDate, false);
                    }
                }}
            />
            {periodErrors.map((error, index) => (
                <IntentText key={index} intent={Intent.DANGER}>{error}</IntentText>
            ))}
        </FormField>
    );
};

const BusinessIncentiveFormBody = ({
    formik,
    close,
    isEditMode,
    isPersistedClosed,
    isEndDateEditable,
}: IBusinessIncentiveFormBodyProps) => {
    const {values, status, isSubmitting, setFieldValue} = formik;
    const isTierStructure = structureRequiresTier(values.structure);
    const requiresValue = structureRequiresValue(values.structure);
    const stateItems = useMemo(() => (
        isTierStructure
            ? [IncentiveState.ACTIVE, IncentiveState.CLOSED]
            : Object.values(IncentiveState)
    ).map(state => ({
        label: incentiveStateLabel[state],
        value: state,
    })), [isTierStructure]);
    const [activeTierIndex, setActiveTierIndex] = useState<number | null>(null);

    useEffect(() => {
        if (isTierStructure && values.state === IncentiveState.INACTIVE) {
            setFieldValue('state', IncentiveState.ACTIVE, false);
        }
    }, [isTierStructure, setFieldValue, values.state]);

    useEffect(() => {
        if (!isTierStructure) {
            if (activeTierIndex !== null) {
                setActiveTierIndex(null);
            }
            if (values.tiers.length > 0) {
                setFieldValue('tiers', [], false);
            }
            if (values.currentTier) {
                setFieldValue('currentTier', '', false);
            }
            return;
        }

        if (values.value !== '') {
            setFieldValue('value', '', false);
            return;
        }

        if (values.tiers.length > 0) {
            const firstTierThreshold = Number(values.tiers[0]?.thresholdUsd ?? 0);
            if (firstTierThreshold !== 0) {
                setFieldValue('tiers', values.tiers.map((tier, index) =>
                    index === 0 ? {...tier, thresholdUsd: 0} : tier
                ), false);
                return;
            }
        }

        if (values.tiers.length === 0) {
            setFieldValue('tiers', [createDefaultTier()], false);
            setFieldValue('currentTier', '', false);
            if (activeTierIndex !== 0) {
                setActiveTierIndex(0);
            }
            return;
        }

        const normalizedCurrentTier = values.currentTier.trim();
        let resolvedActiveIndex = activeTierIndex;
        if (
            resolvedActiveIndex === null ||
            !values.tiers[resolvedActiveIndex] ||
            !values.tiers[resolvedActiveIndex].name.trim()
        ) {
            const matchedTierIndex = values.tiers.findIndex(tier => tier.name.trim() === normalizedCurrentTier);
            resolvedActiveIndex = matchedTierIndex >= 0 ? matchedTierIndex : 0;
        }

        if (activeTierIndex !== resolvedActiveIndex) {
            setActiveTierIndex(resolvedActiveIndex);
            return;
        }

        const resolvedActiveTierName = values.tiers[resolvedActiveIndex]?.name?.trim() ?? '';
        if (resolvedActiveTierName && values.currentTier !== resolvedActiveTierName) {
            setFieldValue('currentTier', resolvedActiveTierName, false);
        }
    }, [activeTierIndex, isTierStructure, setFieldValue, values.currentTier, values.tiers, values.value]);

    const isClosed = isEditMode && isPersistedClosed;
    const immutableInEdit = isEditMode || isSubmitting;
    const mutableDisabled = isSubmitting || isClosed;
    const monetaryLocked = isEditMode && hasStartedPeriod(values.startDate);
    const tierConfigDisabled = mutableDisabled || monetaryLocked;
    const valueDisabled = mutableDisabled || monetaryLocked;
    const scopeDisabled = mutableDisabled;
    const periodDisabled = isSubmitting || isClosed || (isEditMode && !isEndDateEditable);
    const tiersError = formik.submitCount > 0 && typeof formik.errors.tiers === 'string'
        ? formik.errors.tiers
        : null;

    return (
        <Form>
            <FormTitle>{isEditMode ? 'Edit expense' : 'Create expense'}</FormTitle>
            {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
            {isClosed && (
                <IntentText intent={Intent.WARNING}>
                    This expense is closed and cannot be modified.
                </IntentText>
            )}
            <FormRow>
                <FormColumn>
                    <Field
                        name='name'
                        label='Title'
                        options={{
                            placeholder: 'Title',
                            disabled: mutableDisabled,
                        }}
                        component={InputField}
                    />
                    <Field
                        name='incentiveType'
                        label='Type'
                        options={{
                            placeholder: 'Type',
                            items: typeItems,
                            disabled: immutableInEdit,
                        }}
                        component={SelectField}
                    />
                    <Field
                        name='state'
                        label='State'
                        options={{
                            placeholder: 'State',
                            items: stateItems,
                            disabled: mutableDisabled,
                        }}
                        component={SelectField}
                    />
                    <Field
                        name='description'
                        label='Description'
                        options={{
                            placeholder: 'Description',
                            disabled: mutableDisabled,
                            rows: 4,
                        }}
                        component={TextareaField}
                    />
                </FormColumn>
                <FormColumn>
                    <Field
                        name='scope'
                        label='Scope (DSL)'
                        options={{
                            disabled: scopeDisabled,
                            canFullScreen: true,
                            mode: 'javascript',
                            minLines: 4,
                            maxLines: 20
                        }}
                        component={EditorField}
                    />
                    <Field
                        name='startDate'
                        label='Period'
                        options={{
                            disabled: periodDisabled,
                            endDateOnly: isEndDateEditable,
                        }}
                        component={PeriodField}
                    />
                    <Field
                        name='structure'
                        label='Structure'
                        options={{
                            placeholder: 'Structure',
                            items: structureItems,
                            disabled: immutableInEdit,
                        }}
                        component={SelectField}
                    />
                    {requiresValue && (
                        <Field
                            name='value'
                            label='Value (USD)'
                            options={{
                                placeholder: '0.00',
                                min: 0,
                                step: 0.01,
                                disabled: valueDisabled,
                            }}
                            component={UsdInputField}
                        />
                    )}
                </FormColumn>
            </FormRow>
            {isTierStructure && (
                <div className={style.tierSection}>
                    {!!values.tiers.length && (
                        <div className={style.addedTiers}>
                            {values.tiers.map((tier, index) => (
                                <div key={`tier-${index}`} className={style.tiersList}>
                                    <div className={style.tierItemHeader}>
                                        <div className={style.tierItemTitle}>
                                            {tier.name.trim() ? `Tier - ${tier.name.trim()}` : 'Tier'}
                                        </div>
                                        <div className={style.tierItemActions}>
                                            <button
                                                type='button'
                                                className={[
                                                    style.tierActiveToggle,
                                                    activeTierIndex === index ? style.tierActiveToggleOn : ''
                                                ].join(' ')}
                                                disabled={mutableDisabled || !tier.name.trim()}
                                                onClick={() => {
                                                    const tierName = tier.name.trim();
                                                    if (!tierName) {
                                                        return;
                                                    }
                                                    const isActive = activeTierIndex === index;
                                                    if (isActive) {
                                                        return;
                                                    }
                                                    setActiveTierIndex(index);
                                                    setFieldValue('currentTier', tierName, false);
                                                }}
                                            >
                                                <i className={`far fa-toggle-${activeTierIndex === index ? 'on' : 'off'}`}/>
                                                <span>Active</span>
                                            </button>
                                            {index > 0 && (
                                                <button
                                                    type='button'
                                                    className={style.tierDeleteAction}
                                                    disabled={tierConfigDisabled}
                                                    onClick={() => {
                                                        if (index === 0) {
                                                            return;
                                                        }
                                                        const deletedTierName = values.tiers[index]?.name?.trim();
                                                        const nextTiers = values.tiers.filter((_, tierIndex) => tierIndex !== index)
                                                            .map(nextTier => ({
                                                                ...nextTier,
                                                                name: nextTier.name.trim(),
                                                            }));
                                                        setFieldValue('tiers', nextTiers, false);
                                                        if (activeTierIndex === index) {
                                                            const fallbackIndex = nextTiers.length > 0 ? 0 : null;
                                                            setActiveTierIndex(fallbackIndex);
                                                            setFieldValue('currentTier', nextTiers[0]?.name?.trim() ?? '', false);
                                                            return;
                                                        }
                                                        if (activeTierIndex !== null && activeTierIndex > index) {
                                                            setActiveTierIndex(activeTierIndex - 1);
                                                        }
                                                        if (deletedTierName && values.currentTier === deletedTierName && activeTierIndex === null) {
                                                            setFieldValue('currentTier', nextTiers[0]?.name?.trim() ?? '', false);
                                                        }
                                                    }}
                                                >
                                                    <i className='fa fa-trash'/>
                                                    <span>Delete</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className={style.tiersForm}>
                                        <div className={style.tierPrimaryColumn}>
                                            <Field
                                                name={`tiers.${index}.name`}
                                                label='Title'
                                                className={`${style.tierField} ${style.tierFieldName}`}
                                                options={{
                                                    placeholder: 'Title',
                                                    disabled: mutableDisabled,
                                                }}
                                                component={InputField}
                                            />
                                            <Field
                                                name={`tiers.${index}.thresholdUsd`}
                                                label='Threshold USD'
                                                className={`${style.tierField} ${style.tierFieldNumber}`}
                                                options={{
                                                    placeholder: '0.00',
                                                    min: 0,
                                                    step: 0.01,
                                                    disabled: tierConfigDisabled || index === 0,
                                                }}
                                                component={UsdInputField}
                                            />
                                        </div>
                                        <Field
                                            name={`tiers.${index}.percentage`}
                                            label='Percentage (%)'
                                            className={`${style.tierField} ${style.tierFieldPercentage}`}
                                            options={{
                                                placeholder: '0',
                                                min: 0,
                                                max: 100,
                                                step: 0.01,
                                                disabled: tierConfigDisabled,
                                                sliderMin: 0,
                                                sliderMax: 30,
                                                sliderStep: 0.5,
                                                sliderMarkStep: 5,
                                                quickValues: [2, 3, 4, 5, 6],
                                            }}
                                            component={PercentageField}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className={style.addTierBtn}>
                        <Button
                            intent={Intent.PRIMARY}
                            leftIcon={<i className='fa fa-plus'/>}
                            disabled={tierConfigDisabled}
                            onClick={() => {
                                setFieldValue('tiers', [
                                    ...values.tiers,
                                    normalizeTier({name: ''}),
                                ], false);
                            }}
                        >
                            Add tier
                        </Button>
                    </div>
                    {tiersError && <IntentText intent={Intent.DANGER}>{tiersError}</IntentText>}
                </div>
            )}
            <FormControls>
                <Button disabled={isSubmitting} onClick={close}>Cancel</Button>
                <Button
                    type='submit'
                    disabled={isSubmitting || isClosed}
                    intent={Intent.PRIMARY}
                    rightIcon={isSubmitting && <i className='fa fa-spinner fa-spin'/>}
                >
                    Save
                </Button>
            </FormControls>
        </Form>
    );
};

export const BusinessIncentiveForm: IModalComponent<IBusinessIncentiveFormProps> = props => {
    const isEditMode = Boolean(props.incentive?.id);
    const confirm = useConfirm();

    const initialValues = useMemo<IBusinessIncentiveFormValues>(() => {
        const incentive = props.incentive;

        if (!incentive) {
            return {
                id: null,
                incentiveType: IncentiveType.REBATE,
                structure: IncentiveStructure.GROSS_SPEND_TIERS,
                scope: '',
                name: '',
                description: '',
                startDate: '',
                endDate: '',
                state: IncentiveState.ACTIVE,
                currentTier: '',
                tiers: [createDefaultTier()],
                value: '',
            };
        }

        const isTierStructure = structureRequiresTier(incentive.structure);
        const incentiveTiers = Array.isArray(incentive.tiers)
            ? incentive.tiers.map(tier => normalizeTier(tier))
            : [];
        const tiers = isTierStructure
            ? (incentiveTiers.length > 0 ? incentiveTiers : [createDefaultTier()])
            : [];
        const resolvedCurrentTier = isTierStructure
            ? (normalizeCurrentTier(incentive.currentTier).trim() || tiers[0]?.name || '')
            : '';

        return {
            id: incentive.id,
            incentiveType: incentive.incentiveType,
            structure: incentive.structure,
            scope: scopeToFormValue(incentive.scope),
            name: incentive.name,
            description: incentive.description,
            startDate: incentive.startDate,
            endDate: incentive.endDate,
            state: incentive.state,
            currentTier: resolvedCurrentTier,
            tiers,
            value: typeof incentive.value === 'number' && Number.isFinite(incentive.value) ? incentive.value : '',
        };
    }, [props.incentive]);

    const isPersistedClosed = Boolean(props.incentive?.id) && props.incentive?.state === IncentiveState.CLOSED;
    const isEndDateEditable = Boolean(props.incentive?.id) && !isPersistedClosed && isDateInFuture(props.incentive?.endDate);
    const schema = useMemo(() => buildFormSchema(isEditMode, isEndDateEditable), [isEditMode, isEndDateEditable]);
    const closeModal = props.close ?? (() => undefined);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={(values, actions) => {
                const willCloseOnEdit = Boolean(props.incentive?.id)
                    && props.incentive?.state !== IncentiveState.CLOSED
                    && values.state === IncentiveState.CLOSED;

                if (willCloseOnEdit) {
                    actions.setSubmitting(false);
                    confirm('Close this expense? Closed expenses cannot be reopened or edited.', {
                        intent: Intent.WARNING,
                        icon: <i className='fa fa-lock'/>,
                        onOk: () => props.onSubmit?.(values, actions, closeModal),
                        onNo: () => actions.setSubmitting(false),
                    });
                    return;
                }

                props.onSubmit?.(values, actions, closeModal);
            }}
            enableReinitialize
        >
            {formik => (
                <BusinessIncentiveFormBody
                    formik={formik}
                    close={closeModal}
                    isEditMode={isEditMode}
                    isPersistedClosed={isPersistedClosed}
                    isEndDateEditable={isEndDateEditable}
                />
            )}
        </Formik>
    );
};
